import { Link } from "react-router-dom";
import { Reveal } from "./Reveal";
import { EarlyBirdCountdown } from "./EarlyBirdCountdown";
import { useLanguage } from "../i18n/LanguageContext";
import { TICKETCORNER_URL } from "../config";
import { trackInitiateCheckout } from "../analytics";

export const CTAReserva = () => {
    const { t } = useLanguage();
    return (
        <section className="relative" data-testid="cta-reserva-section">
            <Reveal>
                {/* Desktop/Tablet (>=768px): fotografia limpa + coluna de texto à esquerda (8%) */}
                <div className="relative hidden md:block">
                    <div className="absolute inset-x-0 top-0 h-40 sm:h-64 bg-gradient-to-b from-[#070503] via-[#070503]/60 to-transparent z-10 pointer-events-none" data-testid="cta-fade-top" />
                    <img loading="lazy" decoding="async"
                        src="/images/cta-reserva-clean.webp"
                        alt="Mesa posta com guitarra portuguesa, copos de vinho e rosa vermelha"
                        className="w-full block"
                        data-testid="cta-reserva-image"
                    />
                    <div className="absolute inset-x-0 bottom-0 h-40 sm:h-64 bg-gradient-to-t from-[#070503] via-[#070503]/60 to-transparent z-10 pointer-events-none" data-testid="cta-fade-bottom" />
                    <div className="absolute inset-y-0 left-0 w-[62%] bg-gradient-to-r from-[#070503]/75 via-[#070503]/40 to-transparent z-10 pointer-events-none" data-testid="cta-fade-left" />
                    <div className="absolute z-20 inset-y-0 left-[8%] flex flex-col justify-center items-start max-w-[42%]" data-testid="cta-action-block">
                        <h3 className="font-display font-medium gold-text text-[clamp(1.5rem,3.4vw,3.4rem)] leading-[1.08] tracking-[0.03em]" data-testid="cta-titulo">
                            {t.cta.titulo}
                        </h3>
                        <p className="font-display italic font-light text-[clamp(0.95rem,1.6vw,1.6rem)] text-[#efe6d6]/90 mt-[clamp(8px,1.1vw,16px)]" data-testid="cta-subtitulo">
                            {t.cta.subtitulo}
                        </p>
                        <Link
                            to="/bilhetes"
                            data-testid="cta-comprar-bilhetes-button"
                            className="mt-[clamp(16px,2.2vw,32px)] w-[clamp(200px,22vw,340px)] max-w-full text-center font-caps text-[clamp(0.6rem,0.9vw,0.9rem)] tracking-[0.28em] uppercase bg-[#7B1D2A] text-white border border-[#c9a24b]/60 px-[2vw] py-[clamp(0.7rem,1vw,1.05rem)] hover:bg-[#8f2433] transition-colors duration-400"
                        >
                            {t.cta.comprar}
                        </Link>
                        <div data-testid="cta-countdown-row">
                            <EarlyBirdCountdown />
                        </div>
                        <p className="mt-[clamp(10px,1.4vw,20px)] font-caps text-[clamp(0.5rem,0.68vw,0.72rem)] tracking-[0.3em] uppercase text-[#c9a24b]/90" data-testid="cta-urgencia">
                            {t.cta.urgencia}
                        </p>
                        <div className="mt-[clamp(12px,1.7vw,24px)] flex items-center gap-[0.9vw]" data-testid="cta-parceiro">
                            <span className="h-px w-[3vw] bg-[#c9a24b]/60" aria-hidden="true" />
                            <p className="font-caps text-[clamp(0.48rem,0.62vw,0.68rem)] tracking-[0.3em] uppercase text-[#c9a24b]/85 whitespace-nowrap">
                                {t.ticketcorner.label}
                            </p>
                            <span className="h-px w-[3vw] bg-[#c9a24b]/60" aria-hidden="true" />
                        </div>
                        <div className="mt-[clamp(8px,1.1vw,16px)] w-[clamp(200px,22vw,340px)] max-w-full flex justify-center" data-testid="cta-ticketcorner">
                            <a href={TICKETCORNER_URL} target="_blank" rel="noopener noreferrer" className="w-1/2" data-testid="cta-ticketcorner-link" aria-label={`Ticketcorner — ${t.ticketcorner.label}`} onClick={() => trackInitiateCheckout(TICKETCORNER_URL)}>
                                <img loading="lazy" decoding="async" src="/images/ticketcorner-blue.png" alt={`Ticketcorner — ${t.ticketcorner.label}`}
                                    className="w-full h-auto" data-testid="cta-ticketcorner-logo" />
                            </a>
                        </div>
                    </div>
                </div>

                {/* Mobile (<768px): empilhado e centrado */}
                <div className="relative md:hidden" data-testid="cta-mobile">
                    <div className="absolute inset-x-0 top-0 h-32 bg-gradient-to-b from-[#070503] via-[#070503]/70 to-transparent z-10 pointer-events-none" />
                    <img loading="lazy" decoding="async"
                        src="/images/cta-reserva-clean.webp"
                        alt="Mesa posta com guitarra portuguesa, copos de vinho e rosa vermelha"
                        className="w-full aspect-[4/5] object-cover object-[68%_center] block"
                        data-testid="cta-reserva-image-mobile"
                    />
                    <div className="absolute inset-x-0 bottom-0 z-20 px-6 pb-10 pt-32 bg-gradient-to-t from-[#070503] via-[#070503]/85 to-transparent flex flex-col items-center text-center">
                        <h3 className="font-display font-medium gold-text text-3xl leading-[1.1] tracking-[0.04em]" data-testid="cta-titulo-mobile">
                            {t.cta.titulo}
                        </h3>
                        <p className="font-display italic font-light text-lg text-[#efe6d6]/90 mt-4" data-testid="cta-subtitulo-mobile">{t.cta.subtitulo}</p>
                        <Link
                            to="/bilhetes"
                            data-testid="cta-comprar-bilhetes-mobile"
                            className="inline-block mt-8 w-full max-w-[300px] font-caps text-xs tracking-[0.25em] uppercase bg-[#7B1D2A] text-white border border-[#c9a24b]/60 px-8 py-3.5 hover:bg-[#8f2433] transition-colors duration-400"
                        >
                            {t.cta.comprar}
                        </Link>
                        <div data-testid="cta-countdown-row-mobile">
                            <EarlyBirdCountdown />
                        </div>
                        <p className="font-caps text-[9px] tracking-[0.3em] uppercase text-[#c9a24b]/90 mt-5" data-testid="cta-urgencia-mobile">{t.cta.urgencia}</p>
                        <div className="mt-6 flex items-center justify-center gap-3" data-testid="cta-parceiro-mobile">
                            <span className="h-px w-8 bg-[#c9a24b]/60" aria-hidden="true" />
                            <p className="font-caps text-[9px] tracking-[0.25em] uppercase text-[#c9a24b]/85">{t.ticketcorner.label}</p>
                            <span className="h-px w-8 bg-[#c9a24b]/60" aria-hidden="true" />
                        </div>
                        <a href={TICKETCORNER_URL} target="_blank" rel="noopener noreferrer" className="mt-4" data-testid="cta-ticketcorner-link-mobile" aria-label={`Ticketcorner — ${t.ticketcorner.label}`} onClick={() => trackInitiateCheckout(TICKETCORNER_URL)}>
                            <img loading="lazy" decoding="async" src="/images/ticketcorner-blue.png" alt={`Ticketcorner — ${t.ticketcorner.label}`}
                                className="w-[150px] h-auto" data-testid="cta-ticketcorner-logo-mobile" />
                        </a>
                    </div>
                </div>
            </Reveal>
        </section>
    );
};
